// REACT
import { useState, useEffect, useRef, useContext } from "react";
import CreatableSelect from "react-select/creatable";

// ASSETS
import { Button, Alert, Spinner } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { FaFileExcel, FaUpload } from "react-icons/fa";
import "../../assets/styles/upload.css";
import "../../assets/styles/templates.css";
import languagePack from "../../assets/languages/languagePack";
import { Form } from "react-bootstrap";
import { Collapse } from "react-bootstrap";

// COMPONENTS
import TemplateList from "../common/TemplateList";
import Loading from "../common/Loading";

// CUSTOM HOOKS
import { useDownload, useRead, useUpload } from "../../hooks/useFetch";

// CONTEXT
import { maincontext } from "../../context/maincontext";

export default function Upload(props) {
    const { language, token, isAdmin } = useContext(maincontext);

    // Input / DataPackages
    const [distributorSelectOptions, setDistributorSelectOptions] = useState([]);
    const { selectedDistributor, onSelectDistributor } = props;
    const [selectedFile, setSelectedFile] = useState(null);
    const [dataToUpload, setDataToUpload] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(null); // State for success message

    const fileInputRef = useRef(null);

    // ERROR VARS
    const [errors, setErrors] = useState({});

    // Fetch
    const { data, isPending, error } = useUpload(dataToUpload, props.uploadUrl);

    // FETCH DISTRIBUTORS
    const distributorOptionsUrl = isAdmin ? `${process.env.REACT_APP_BACKEND_BASE_URL}distributors` : null
    const distributorOptions = useRead(distributorOptionsUrl, null)

    // Allowed file types and size
    const allowedFileTypes = ["csv", "xls", "xlsx"];
    const maxFileSize = 100 * 1024 * 1024; // 5MB

    // Template states
    const [showTemplates, setShowTemplates] = useState(false);

    // Template download states
    const [downloadRequest, setDownloadRequest] = useState(null);
    const [downloadingFile, setDownloadingFile] = useState(null);

    // Template download fetch
    const {
        isPending: isDownloading,
        error: downloadError
    } = useDownload(
        null,
        downloadRequest?.url,
        downloadRequest?.filename,
        "GET", // or props.templateDownloadMethod
        downloadRequest?.trigger
    );

    // File download
    const handleTemplateDownload = (filename) => {
        setDownloadingFile(filename);
        setDownloadRequest({
            url: `${props.templateDownloadBaseUrl}/${filename}`,
            filename,
            trigger: Date.now()
        });
    };

    // Spinner animation delay and reset
    useEffect(() => {
        if (!isDownloading && downloadingFile) {
            // Add a brief delay to allow spinner to appear visually
            const timeout = setTimeout(() => {
                setDownloadingFile(null);
            }, 400); // ~400ms ensures visibility but feels snappy

            return () => clearTimeout(timeout);
        }

        // eslint-disable-next-line
    }, [isDownloading]);

    // // Handle file validation
    // function validateFile(file) {
    //     if (!file) return false;

    //     const fileExtension = file.name.split(".").pop().toLowerCase();
    //     return allowedFileTypes.includes(fileExtension);
    // }

    // DropZone Configuration
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            "text/csv": [".csv"],
            "application/vnd.ms-excel": [".xls"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"]
        },
        multiple: false,
        onDrop: (acceptedFiles) => {
            const file = acceptedFiles[0];

            if (!file) return;

            const fileExtension = file.name.split(".").pop().toLowerCase();

            if (!allowedFileTypes.includes(fileExtension)) {
                setSelectedFile(null);
                setErrors({ selectedFile: languagePack[language].uploadManager.invalidType });
                setUploadSuccess(null);
                return;
            }

            if (file.size > maxFileSize) {
                setSelectedFile(null);
                setErrors({ selectedFile: `${languagePack[language].uploadManager.fileTooLarge}` });
                setUploadSuccess(null);
                return;
            }

            setSelectedFile(file);
            setErrors({});
            setUploadSuccess(null);
        }

    });

    // Upload handler
    function handleUpload() {
        const newErrors = {};

        // Validate distributor only if props are passed and if isAdmin
        if (
            isAdmin &&
            typeof selectedDistributor !== "undefined" &&
            typeof onSelectDistributor === "function" &&
            (!selectedDistributor || !selectedDistributor.value?.trim())
        ) {
            newErrors.selectedDistributor = languagePack[language].uploadManager.distributorValidation;
        }

        // Validate file
        if (!selectedFile) {
            newErrors.selectedFile = languagePack[language].uploadManager.noFile;
        }

        // If errors exist, update state and exit
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Proceed with upload
        setErrors({});
        const formData = new FormData();
        formData.append("file", selectedFile);
        setDataToUpload(formData);
    }

    // DISTRIBUTORS
    useEffect(() => {
        if (!distributorOptions.isPending && distributorOptions.data) {
            setDistributorSelectOptions((distributorOptions.data).map(distributor => ({
                value: distributor,
                label: distributor
            })))
        }
    }, [distributorOptions.isPending, distributorOptions.error, distributorOptions.data])

    // Clear files after upload
    useEffect(() => {
        if (data && !isPending) {
            setSelectedFile(null);
            setDataToUpload(null);
            setErrors({}); // Clear errors on success
            setUploadSuccess(languagePack[language].uploadManager.uploadSuccess); // Show success message
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }

        // eslint-disable-next-line
    }, [data, isPending]);

    return (
        <>
            {token !== null && !distributorOptions.isPending ?
                <Form>
                    <div className="upload-container">
                        <h3>{languagePack[language].uploadManager.formTitle}</h3>

                        {/* Template Section: Toggle + Collapsible List */}
                        {props.templateFetchUrl && props.templateDownloadBaseUrl && (
                            <div className="template-section mt-4 mb-4">
                                <div className="template-toggle text-center mb-3">
                                    <Button
                                        variant="light"
                                        onClick={() => setShowTemplates(!showTemplates)}
                                        aria-expanded={showTemplates}
                                        className="template-dropdown-toggle d-flex justify-content-between align-items-center"
                                    >
                                        <span>
                                            {showTemplates
                                                ? languagePack[language].templatelist.hideTemplates
                                                : languagePack[language].templatelist.showTemplates}
                                        </span>
                                        <span>{showTemplates ? "▲" : "▼"}</span>
                                    </Button>
                                </div>

                                <Collapse in={showTemplates}>
                                    <div>
                                        <TemplateList
                                            fetchUrl={props.templateFetchUrl}
                                            onDownload={handleTemplateDownload}
                                            isDownloadingFile={downloadingFile}
                                            downloadError={downloadError}
                                        />
                                    </div>
                                </Collapse>
                            </div>
                        )}

                        {/* Drag & Drop Zone */}
                        <div {...getRootProps()} className={`dropzone ${isDragActive ? "active" : ""}`}>
                            <input {...getInputProps()} />

                            {selectedFile ? (
                                <div className="file-preview">
                                    <FaFileExcel size={50} color="#217346" /> {/* Excel icon */}
                                    <p>{selectedFile.name}</p> {/* File name */}
                                </div>
                            ) : isDragActive ? (
                                <p>{languagePack[language].uploadManager.dropHere}</p>
                            ) : (
                                <p>{languagePack[language].uploadManager.dragDropHere}</p>
                            )}
                        </div>

                        {/* DISTRIBUTORS */}
                        {isAdmin && typeof selectedDistributor !== "undefined" && typeof onSelectDistributor === "function" && (
                            <>
                                <Form.Group className="mt-5">
                                    <Form.Label className="formLabel">
                                        {languagePack[language].uploadManager.distributorLabel}
                                        <span className="required">*</span>
                                    </Form.Label>
                                    <div className="react-select-wrapper">
                                        <CreatableSelect
                                            isClearable
                                            isSearchable
                                            options={distributorSelectOptions}
                                            value={selectedDistributor}
                                            onChange={(selected) => {
                                                onSelectDistributor(selected);
                                            }}
                                            placeholder={languagePack[language].uploadManager.distributorPlaceholder}
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </Form.Group>
                                {errors?.selectedDistributor && (
                                    <div className="invalid-feedback d-block mt-1">{errors.selectedDistributor}</div>
                                )}
                            </>
                        )}

                        {/* Error Message for Invalid File */}
                        {errors.selectedFile && (
                            <Alert variant="danger" className="mt-3">{errors.selectedFile}</Alert>
                        )}

                        {/* Upload Button */}
                        <Button
                            variant="primary"
                            onClick={handleUpload}
                            disabled={
                                isPending ||
                                !selectedFile ||
                                (
                                    isAdmin &&
                                    typeof selectedDistributor !== "undefined" &&
                                    typeof onSelectDistributor === "function" &&
                                    (!selectedDistributor || !selectedDistributor.value)
                                )
                            }
                        >
                            {isPending ? (
                                <>
                                    <Spinner animation="border" size="sm" className="loadingAnimation" />{languagePack[language].uploadManager.uploading}
                                </>
                            ) : (
                                <div className="uploadButtonText">
                                    <FaUpload className="text-white uploadIcon" />&nbsp;{languagePack[language].uploadManager.upload}
                                </div>
                            )}
                        </Button>

                        {/* Success/Error Messages */}
                        {error && (
                            <Alert variant="danger" className="mt-3">
                                {typeof error === "string" && error.includes("Please ensure the file contains all required columns")
                                    ? languagePack[language].uploadManager.missingColumns
                                    : error}
                            </Alert>
                        )}
                        {uploadSuccess && data && !isPending && <Alert variant="success" className="mt-3">{languagePack[language].uploadManager.uploadSuccess}</Alert>}
                    </div>
                </Form>
                :
                <Loading />
            }
        </>
    );
}
